import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Users, Download, Mail } from "lucide-react";

const Card = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">{value}</div>
  </div>
);

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function EposReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [storeId, setStoreId] = useState("");
  const [report, setReport] = useState(null);
  const [rLoading, setRLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get("/epos/reports/summary"),
          api.get("/epos/stores"),
        ]);
        setData(s.data);
        setStores(st.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load reports");
      } finally { setLoading(false); }
    })();
  }, []);

  const loadReport = useCallback(async () => {
    setRLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (storeId) params.store_id = storeId;
      const r = await api.get("/epos/reports/by-salesperson", { params });
      setReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setRLoading(false); }
  }, [from, to, storeId]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const downloadPdf = async () => {
    try {
      const params = { date_from: from, date_to: to };
      if (storeId) params.store_id = storeId;
      const r = await api.get("/epos/reports/by-salesperson.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "SalesBySalesperson.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const emailReport = async () => {
    try {
      const r = await api.post("/epos/reports/email", { kind: "salesperson", date_from: from, date_to: to, store_id: storeId || undefined });
      toast.success(`Emailed to ${(r.data.sent_to || []).join(", ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to email report"); }
  };

  const t = report?.totals;

  return (
    <div className="w-full px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="epos-reports">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
        <PageIcon /> EPOS Reports
      </h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Read-only overview of your EPOS estate.</p>

      {loading ? (
        <div className="text-slate-400 text-sm py-12 text-center">Loading…</div>
      ) : data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card label="Stores" value={data.stores} testId="epos-report-stores" />
          <Card label="Registers" value={data.registers} testId="epos-report-registers" />
          <Card label="Open sessions" value={data.open_sessions} testId="epos-report-open-sessions" />
          <Card label="Total sessions" value={data.total_sessions} testId="epos-report-total-sessions" />
        </div>
      ) : null}

      <div className="mt-8" data-testid="epos-salesperson-report">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> Sales by salesperson
          </h2>
          <div className="flex gap-2">
            <button onClick={downloadPdf} data-testid="report-pdf-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={emailReport} data-testid="report-email-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
              <Mail className="w-4 h-4" /> Email report
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-3 mb-4">
          <label className="text-xs text-slate-500">From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="block mt-1 h-9 rounded-lg border border-slate-200 px-2.5 text-sm" data-testid="report-from" />
          </label>
          <label className="text-xs text-slate-500">To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="block mt-1 h-9 rounded-lg border border-slate-200 px-2.5 text-sm" data-testid="report-to" />
          </label>
          <label className="text-xs text-slate-500">Store
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)}
              className="block mt-1 h-9 rounded-lg border border-slate-200 px-2.5 text-sm min-w-[160px]" data-testid="report-store">
              <option value="">All stores</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5">Salesperson</th>
                <th className="text-right px-4 py-2.5">Sales</th>
                <th className="text-right px-4 py-2.5">Gross</th>
                <th className="text-right px-4 py-2.5">Discounts</th>
                <th className="text-right px-4 py-2.5">VAT</th>
                <th className="text-right px-4 py-2.5">Refunds</th>
                <th className="text-right px-4 py-2.5">Net</th>
              </tr>
            </thead>
            <tbody>
              {rLoading ? (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">Loading…</td></tr>
              ) : (report?.rows?.length ? report.rows.map((r) => (
                <tr key={r.salesperson_id || r.salesperson} className="border-t border-slate-100" data-testid={`report-row-${r.salesperson}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.salesperson}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{r.transaction_count}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{gbp(r.gross_sales)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{gbp(r.discounts)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{gbp(r.vat)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-rose-600">{gbp(r.refunds)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{gbp(r.net_sales)}</td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">No sales in this range</td></tr>
              ))}
            </tbody>
            {t && report?.rows?.length ? (
              <tfoot className="bg-slate-50 font-semibold text-slate-800">
                <tr data-testid="report-totals">
                  <td className="px-4 py-2.5">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{t.transaction_count}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{gbp(t.gross_sales)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{gbp(t.discounts)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{gbp(t.vat)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-rose-600">{gbp(t.refunds)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{gbp(t.net_sales)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>
    </div>
  );
}
